
import React, { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/ui/icon'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CompactSlider,
  CompactNumber,
  CompactSelect,
  CompactToggle,
  CompactInput,
  CompactSeed,
} from './compact-controls'
import type { MergedParameter } from '@/lib/ai/parameter-merge'

// ============================================================================
// TYPES
// ============================================================================

interface PinnedModelOption {
  id: string
  name: string
}

interface PinnedParametersStripProps {
  pinnedParameters: string[]
  parameters: MergedParameter[]
  values: Record<string, unknown>
  onChange: (paramName: string, value: unknown) => void
  /** Model name to display when model is pinned */
  modelName?: string
  /** Available models for model dropdown */
  models?: PinnedModelOption[]
  /** Callback when model is changed via dropdown */
  onModelChange?: (modelId: string) => void
}

// ============================================================================
// MAX VISIBLE BEFORE SCROLL
// ============================================================================

const MAX_VISIBLE_PARAMETERS = 6

// ============================================================================
// CONTROL DISPATCHER
// ============================================================================

function getControlForParameter(parameter: MergedParameter) {
  switch (parameter.uiType) {
    case 'slider':
      return CompactSlider
    case 'number':
      return CompactNumber
    case 'select':
    case 'aspect-ratio':
    case 'image-size':
    case 'select-or-input':
      return CompactSelect
    case 'toggle':
      return CompactToggle
    case 'seed':
      return CompactSeed
    case 'input':
    case 'textarea':
    default:
      return CompactInput
  }
}

// ============================================================================
// MODEL DROPDOWN (inline mini-popover)
// ============================================================================

function ModelDropdown({
  modelName,
  models,
  onModelChange,
}: {
  modelName: string
  models: PinnedModelOption[]
  onModelChange: (modelId: string) => void
}) {
  // Find current model id from name
  const currentModelId = models.find(m => m.name === modelName)?.id || models[0]?.id || ''

  return (
    <div className="nodrag nopan">
      <Select value={currentModelId} onValueChange={onModelChange}>
        <SelectTrigger size="sm" className="h-7 font-mono text-[13px] border-border/50 bg-muted text-foreground hover:bg-black/[0.08] dark:hover:bg-white/[0.08] min-w-0">
          <span className="truncate"><SelectValue placeholder="Select model..." /></span>
        </SelectTrigger>
        <SelectContent>
          {models.map((model) => (
            <SelectItem key={model.id} value={model.id}>
              {model.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

// ============================================================================
// PINNED PARAMETERS STRIP
// ============================================================================

function PinnedParametersStripComponent({
  pinnedParameters,
  parameters,
  values,
  onChange,
  modelName,
  models,
  onModelChange,
}: PinnedParametersStripProps) {
  // Build a map of parameter name -> MergedParameter for fast lookup
  const parameterMap = useMemo(() => {
    const map = new Map<string, MergedParameter>()
    for (const param of parameters) {
      map.set(param.name, param)
    }
    return map
  }, [parameters])

  // Filter to only pinned parameters that exist in the parameter definitions
  const resolvedParams = useMemo(() => {
    const result: MergedParameter[] = []
    for (const name of pinnedParameters) {
      const param = parameterMap.get(name)
      if (param && !param.hidden) {
        result.push(param)
      }
    }
    return result
  }, [pinnedParameters, parameterMap])

  // Pre-compute stable change handlers per param — avoids creating new closures on every render
  // which would defeat React.memo on the compact control components
  const changeHandlers = useMemo(() => {
    const handlers: Record<string, (value: unknown) => void> = {}
    resolvedParams.forEach(param => {
      handlers[param.name] = (value: unknown) => onChange(param.name, value)
    })
    return handlers
  }, [resolvedParams, onChange])

  // Don't render if no pinned parameters and no model pinned
  if (resolvedParams.length === 0 && !modelName) return null

  const needsScroll = resolvedParams.length > MAX_VISIBLE_PARAMETERS

  return (
    <div
      className="mx-2 mb-1 nodrag nopan"
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div>
        {/* Always-visible parameter list */}
        <div
          className={cn(
            'px-1 py-1 space-y-0.5'
          )}
        >
          {/* Pinned model display / dropdown */}
          {modelName && (
            <div className="flex items-center gap-2 h-7 nodrag nopan">
              <div className="w-[38%] min-w-0">
                <div className="flex items-center gap-1.5 truncate" title="Model">
                  <Icon name="cpu" className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="text-[11px] font-mono font-medium uppercase tracking-wider text-muted-foreground truncate leading-none">
                    Model
                  </span>
                </div>
              </div>
              <div className="w-[62%]">
                {models && models.length > 0 && onModelChange ? (
                  <ModelDropdown
                    modelName={modelName}
                    models={models}
                    onModelChange={onModelChange}
                  />
                ) : (
                  <span className="font-mono text-[13px] font-medium text-foreground truncate block px-2" title={modelName}>
                    {modelName}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Separator between model and params */}
          {modelName && resolvedParams.length > 0 && (
            <div className="border-t border-black/[0.06] dark:border-white/[0.06] !my-1.5" />
          )}

          {resolvedParams.map((param) => {
            const Control = getControlForParameter(param)
            return (
              <Control
                key={param.name}
                label={param.label}
                value={values[param.name]}
                onChange={changeHandlers[param.name]}
                parameter={param}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

export const PinnedParametersStrip = React.memo(PinnedParametersStripComponent)
PinnedParametersStrip.displayName = 'PinnedParametersStrip'
